import classNames from "classnames"
import { useField } from "formik"

import InputError from "./InputError"
import { Color } from "../../../../constants/V2/color"
import Typography from "../Typography"

import { getTextColorClass } from "@utils/V2/color"
import React, { useState, useEffect, ReactNode } from "react"

// TODO: Whole input, styling and props is 99% the same as Phone number. Look into refactoring to one code

const ICON_POSITION_CLASSES = {
  left: "left-16",
  right: "right-4",
}

const ICON_PADDING_CLASSES = {
  left: "pl-60 pr-16",
  right: "pl-16 pr-60",
}

export interface Props {
  id: string
  name: string
  maxLength?: number
  required?: boolean
  disabled?: boolean
  className?: string
  whiteBackground?: boolean
  isRounded?: boolean
  icon?: ReactNode
  iconPosition?: "left" | "right"
  label?: string
  placeholder?: string
  color?: Color.White | Color.Charcoal
  onFocus?: () => void
  description?: string
  "data-testid"?: string
  type: "text" | "password" | "email"
  children?: ReactNode
}

const TextInput = ({
  id,
  name,
  type,
  onFocus,
  className,
  whiteBackground = false,
  isRounded = false,
  icon,
  iconPosition = "left",
  label,
  placeholder,
  description,
  color = Color.Charcoal,
  maxLength = 250,
  disabled = false,
  required = false,
  /**
   * Children will render relative to the input
   */
  children,
  "data-testid": dataTestId,
}: Props) => {
  const [focus, setFocus] = useState(false)
  const [field, meta, helpers] = useField(id)
  const [showPassword, setShowPassword] = useState(false)

  const hasError = meta.touched && meta.error
  const hasValueOrFocus = !!field.value || focus

  useEffect(() => {
    helpers.setTouched(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={classNames("w-full", className)}>
      <div
        className={classNames("relative w-full", {
          "rounded-32": isRounded,
          "rounded-10": !isRounded,
        })}
      >
        {label && (
          <label
            htmlFor={id}
            className={classNames(
              "pointer-events-none absolute left-16 flex gap-8 transition-all duration-200 ease-linear",
              {
                "!top-8": hasValueOrFocus,
              },
              {
                "top-20": !hasValueOrFocus,
              }
            )}
          >
            <Typography
              weight={hasValueOrFocus ? "medium" : "book"}
              font="grotesk"
              color={color}
              text={required ? `${label} *` : label}
              size={hasValueOrFocus ? "subscript-sm" : "body-sm"}
              className="transition-all duration-200"
            />
            {description && (
              <Typography
                weight="book"
                font="grotesk"
                text={`(${description})`}
                color={color}
                size={hasValueOrFocus ? "subscript-sm" : "body-sm"}
              />
            )}
          </label>
        )}
        <div>
          {icon && (
            <div
              className={classNames(
                "pointer-events-none absolute top-1/2 -translate-y-1/2",
                ICON_POSITION_CLASSES[iconPosition]
              )}
            >
              {icon}
            </div>
          )}
          <input
            id={id}
            {...field}
            data-testid={dataTestId}
            name={name}
            value={field.value}
            disabled={disabled}
            maxLength={maxLength}
            onBlurCapture={() => setFocus(false)}
            type={type === "password" && showPassword ? "text" : type}
            placeholder={
              label
                ? hasValueOrFocus && placeholder
                  ? placeholder
                  : undefined
                : placeholder
            }
            className={classNames(
              whiteBackground ? "bg-white" : "bg-transparent",
              "w-full border px-16 py-[18px] text-body-sm focus:border-blue",
              icon ? ICON_PADDING_CLASSES[iconPosition] : "px-16",
              isRounded ? "rounded-32" : "rounded-10",
              getTextColorClass(color),
              { "border-orange": hasError },
              {
                "border-charcoal/20 hover:border-charcoal":
                  color === Color.Charcoal && !hasError,
              },
              {
                "border-white/20 hover:border-white":
                  color === Color.White && !hasError,
              }
            )}
            onFocus={() => {
              setFocus(true)
              if (onFocus) {
                onFocus()
              }
            }}
          />
          {!!children && (
            <div className="absolute bottom-0 right-0 top-0">{children}</div>
          )}
        </div>

        {type === "password" && (
          <span
            onMouseUp={() => setShowPassword(false)}
            onMouseDown={() => setShowPassword(true)}
            className="absolute right-16 top-[22px] cursor-pointer hover:opacity-50"
          >
            <Typography
              text="show"
              weight="book"
              font="grotesk"
              size="body-sm"
              color={color}
            />
          </span>
        )}
      </div>

      {hasError ? (
        <div className="flex items-start">
          <InputError
            error={`${meta.error}`}
            data-testid={`input-error-${dataTestId}`}
          />
        </div>
      ) : null}
    </div>
  )
}

export default TextInput
